'use client';

/**
 * HkWealthGrowthChart
 * Animated, interactive chart panel: HK total AUM, private wealth AUM (SFC
 * AWMAS 2019–2024) and single-family office counts (Deloitte/InvestHK).
 *
 * Install:  npm i chart.js
 * Usage:    <HkWealthGrowthChart />           — defaults to Total AUM view
 *           <HkWealthGrowthChart defaultSeries="sfo" className="my-12" />
 *
 * Font: expects Poppins to be loaded in the app, e.g. via next/font:
 *   import { Poppins } from 'next/font/google';
 *   const poppins = Poppins({ weight: ['400','500'], subsets: ['latin'] });
 * or a Google Fonts <link>. Falls back to sans-serif if absent.
 *
 * Self-contained: inline styles only (no Tailwind dependency), hard white
 * panel so it renders identically on any background. Re-skin via THEME.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Chart,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js';

Chart.register(
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip
);

const THEME = {
  panelBg: '#ffffff',
  panelBorder: '#e3e2dc',
  ink: '#1a1a1a',
  inkSecondary: '#5f5e5a',
  inkMuted: '#898781',
  grid: '#efeee9',
  axis: '#e3e2dc',
  primary: '#0f9993', // brand teal (blue-500)
  primaryTint: '#ecfbf9', // brand teal-50
  primaryDark: '#04445c', // brand deep teal (blue-900)
  secondary: '#bda154', // brand gold (yellow-500)
  greenDark: '#0d555c', // dark teal-green (blue-800) — bold hero value on green panels
  goldDark: '#8a6d1f', // dark gold — bold hero value on the gold panel
  radius: 16,
  font: "'Poppins', sans-serif",
};

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

type SeriesKey = 'total' | 'pbpwm' | 'sfo';

interface SeriesDef {
  chartType: 'line' | 'bar';
  labels: string[];
  data: number[];
  color: string;
  fill?: string;
  heroLabel: string;
  heroValue: number;
  formatHero: (v: number) => string;
  heroColor: string;
  delta: string;
  deltaColor: string;
  deltaBg: string;
  source: string;
  yMax: number;
  formatTick: (v: number) => string;
  formatTip: (v: number) => string;
}

const SERIES: Record<SeriesKey, SeriesDef> = {
  total: {
    chartType: 'line',
    labels: ['2019', '2020', '2021', '2022', '2023', '2024'],
    data: [28.8, 34.9, 35.5, 30.5, 31.2, 35.1],
    color: THEME.primary,
    fill: 'rgba(15, 153, 147, 0.10)',
    heroLabel: 'Total asset & wealth management AUM, end-2024',
    heroValue: 35.1,
    formatHero: (v) => `HK$${v.toFixed(1)}T`,
    heroColor: THEME.greenDark,
    delta: '+13% YoY',
    deltaColor: THEME.primaryDark,
    deltaBg: THEME.primaryTint,
    source:
      'Source: SFC Asset and Wealth Management Activities Survey, 2019\u20132024. Year-end figures, HK$ trillions.',
    yMax: 40,
    formatTick: (v) => `HK$${v}T`,
    formatTip: (v) => `HK$${v.toFixed(1)} trillion`,
  },
  pbpwm: {
    chartType: 'line',
    labels: ['2019', '2020', '2021', '2022', '2023', '2024'],
    data: [9.1, 11.3, 10.6, 9.0, 9.0, 10.4],
    color: THEME.secondary,
    fill: 'rgba(189, 161, 84, 0.12)',
    heroLabel: 'Private banking & private wealth management AUM, end-2024',
    heroValue: 10.4,
    formatHero: (v) => `HK$${v.toFixed(1)}T`,
    heroColor: THEME.goldDark,
    delta: '+15% YoY',
    deltaColor: THEME.primaryDark,
    deltaBg: THEME.primaryTint,
    source:
      'Source: SFC Asset and Wealth Management Activities Survey, 2019\u20132024. Year-end figures, HK$ trillions.',
    yMax: 14,
    formatTick: (v) => `HK$${v}T`,
    formatTip: (v) => `HK$${v.toFixed(1)} trillion`,
  },
  sfo: {
    chartType: 'bar',
    labels: ['End-2023', 'End-2025'],
    data: [2703, 3384],
    color: THEME.primary,
    heroLabel: 'Single-family offices in Hong Kong, end-2025',
    heroValue: 3384,
    formatHero: (v) => Math.round(v).toLocaleString(),
    heroColor: THEME.greenDark,
    delta: '+25% in 2 years',
    deltaColor: THEME.primaryDark,
    deltaBg: THEME.primaryTint,
    source:
      'Source: Deloitte market studies commissioned by InvestHK. The end-2023 baseline is the first count ever made; no earlier data exists.',
    yMax: 4000,
    formatTick: (v) => v.toLocaleString(),
    formatTip: (v) => `${Math.round(v).toLocaleString()} single-family offices`,
  },
};

const TAB_LABELS: Record<SeriesKey, string> = {
  total: 'Total AUM',
  pbpwm: 'Private wealth AUM',
  sfo: 'Family offices',
};

interface HkWealthGrowthChartProps {
  defaultSeries?: SeriesKey;
  className?: string;
}

function useInView(ref: { readonly current: HTMLCanvasElement | null }): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

export default function HkWealthGrowthChart({
  defaultSeries = 'total',
  className,
}: HkWealthGrowthChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const rafRef = useRef<number>(0);
  const inView = useInView(canvasRef);
  const [active, setActive] = useState<SeriesKey>(defaultSeries);
  const [heroText, setHeroText] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);

  const prefersReducedMotion = useCallback(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Mobile gets top/bottom rules only (more horizontal room) and no logo.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!inView) return;
    const s = SERIES[active];
    const reduced = prefersReducedMotion();

    cancelAnimationFrame(rafRef.current);
    if (reduced) {
      setHeroText(s.formatHero(s.heroValue));
    } else {
      const duration = 1100;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setHeroText(s.formatHero(s.heroValue * eased));
        if (t < 1) rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    }

    if (!canvasRef.current) return;
    chartRef.current?.destroy();

    const isLine = s.chartType === 'line';
    chartRef.current = new Chart(canvasRef.current, {
      type: s.chartType,
      data: {
        labels: s.labels,
        datasets: [
          {
            data: s.data,
            borderColor: s.color,
            backgroundColor: isLine ? s.fill : s.color,
            borderWidth: 2.5,
            pointRadius: isLine ? 5 : 0,
            pointHoverRadius: isLine ? 7 : 0,
            pointBackgroundColor: s.color,
            pointBorderColor: THEME.panelBg,
            pointBorderWidth: 2,
            fill: isLine,
            tension: 0.2,
            borderRadius: isLine ? 0 : { topLeft: 5, topRight: 5 },
            maxBarThickness: 84,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        animation: reduced ? false : { duration: 900, easing: 'easeOutQuart' },
        animations: reduced
          ? undefined
          : isLine
            ? {
                y: {
                  from: (ctx: any) => ctx.chart.scales.y.getPixelForValue(0),
                  delay: (ctx: any) =>
                    ctx.type === 'data' ? ctx.dataIndex * 130 : 0,
                },
              }
            : {
                // Grow bars up from the zero baseline, staggered per bar.
                y: {
                  from: (ctx: any) => ctx.chart.scales.y.getPixelForValue(0),
                  delay: (ctx: any) =>
                    ctx.type === 'data' ? ctx.dataIndex * 220 : 0,
                },
                base: {
                  from: (ctx: any) => ctx.chart.scales.y.getPixelForValue(0),
                  delay: (ctx: any) =>
                    ctx.type === 'data' ? ctx.dataIndex * 220 : 0,
                },
              },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: THEME.ink,
            titleColor: '#ffffff',
            bodyColor: '#e8e7e0',
            titleFont: { family: THEME.font },
            bodyFont: { family: THEME.font },
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: { label: (c) => s.formatTip(c.parsed.y as number) },
          },
        },
        scales: {
          y: {
            min: 0,
            max: s.yMax,
            ticks: {
              color: THEME.inkMuted,
              font: { size: 12, family: THEME.font },
              callback: (v) => s.formatTick(Number(v)),
            },
            grid: { color: THEME.grid },
            border: { color: THEME.axis },
          },
          x: {
            ticks: {
              color: THEME.inkMuted,
              font: { size: 12, family: THEME.font },
              autoSkip: false,
            },
            grid: { display: false },
            border: { color: THEME.axis },
          },
        },
      },
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [active, prefersReducedMotion, inView]);

  const s = SERIES[active];

  // Keep the "HK$" currency prefix at the base weight; bold the numeric value.
  const heroPrefix = heroText.startsWith('HK$') ? 'HK$' : '';
  const heroValue = heroText.slice(heroPrefix.length);

  return (
    <div
      className={className}
      style={{
        background: THEME.panelBg,
        borderTop: `1px solid ${THEME.panelBorder}`,
        borderBottom: `1px solid ${THEME.panelBorder}`,
        borderLeft: isMobile ? 'none' : `1px solid ${THEME.panelBorder}`,
        borderRight: isMobile ? 'none' : `1px solid ${THEME.panelBorder}`,
        borderRadius: isMobile ? 0 : THEME.radius,
        padding: isMobile ? '1.5rem 0' : '1.5rem 1.75rem',
        color: THEME.ink,
        fontFamily: THEME.font,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 13,
              color: THEME.inkMuted,
              letterSpacing: '0.02em',
            }}
          >
            {s.heroLabel}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span
              style={{
                fontSize: 40,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums lining-nums',
              }}
              aria-live="polite"
            >
              {heroPrefix}
              <span style={{ fontWeight: 700, color: s.heroColor }}>{heroValue}</span>
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: s.deltaColor,
                background: s.deltaBg,
                padding: '3px 10px',
                borderRadius: 999,
              }}
            >
              {s.delta}
            </span>
          </div>
        </div>
        {!isMobile ? (
          <img
            src={PSGLOBAL_LOGO}
            alt="PS Global Consulting"
            style={{ height: 44, width: 'auto', flexShrink: 0, alignSelf: 'flex-start', margin: 0 }}
          />
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label="Data series"
        style={{
          display: 'flex',
          gap: 6,
          margin: '28px 0 18px',
          flexWrap: 'wrap',
        }}
      >
        {(Object.keys(SERIES) as SeriesKey[]).map((key) => {
          const isActive = key === active;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(key)}
              style={{
                border: `1px solid ${isActive ? THEME.primary : '#d9d8d3'}`,
                background: isActive ? THEME.primaryTint : THEME.panelBg,
                color: isActive ? THEME.primaryDark : THEME.inkSecondary,
                borderRadius: 999,
                padding: '7px 16px',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {TAB_LABELS[key]}
            </button>
          );
        })}
      </div>

      <div style={{ position: 'relative', width: '100%', height: 300 }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${s.heroLabel}. ${s.labels
            .map((l, i) => `${l}: ${s.formatTip(s.data[i])}`)
            .join('; ')}.`}
        />
      </div>

      <div
        style={{
          fontSize: 12,
          color: THEME.inkMuted,
          marginTop: 12,
          lineHeight: 1.5,
        }}
      >
        {s.source}
      </div>
    </div>
  );
}
